import { useState } from "react";
import { Flex, Heading, Modal, Paragraph, Button } from "@ir/ui";
import { Credit } from "@/core/entities/credit";
import DocumentUploadForm from "./DocumentUploadForm";
import { useCreditDocumentUpload } from "./hook";
import { CreditUploadDocument } from "./atom/documentUpload";
import { getAttachableCreditDocuments } from "./creditDocumentUpload.utils";

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  credit: Credit;
}

const ModalHeader = () => {
  return (
    <Flex>
      <Heading level={6} className="font-bold">
        Upload and Attach Documents
      </Heading>
      <Paragraph className="text-grey-500 m-0">Upload and attach documents to the credit</Paragraph>
    </Flex>
  );
};

interface ModalFooterProps {
  onClose: () => void;
  isAttaching: boolean;
  documents: CreditUploadDocument[];
  onAttach: () => void;
  isDocumentAttached: boolean;
}

const ModalFooter = ({ onClose, isAttaching, documents, onAttach, isDocumentAttached }: ModalFooterProps) => {
  const hasAttachableDocuments = getAttachableCreditDocuments(documents).length > 0;
  const isDocumentUploadBusy = documents.some(document => document.isPending || document.isUploading);

  return (
    <Flex gap={0.75} className="w-100">
      <Button variant="outline" color="grey" className="flex-1" onPress={onClose}>
        Cancel
      </Button>
      <Button
        variant="solid"
        color="primary"
        className="flex-1"
        onPress={onAttach}
        isDisabled={!hasAttachableDocuments || isDocumentUploadBusy || isAttaching || isDocumentAttached}
        isLoading={isAttaching}
      >
        Attach Documents
      </Button>
    </Flex>
  );
};

const DocumentUploadModal = ({ isOpen, onClose, credit }: DocumentUploadModalProps) => {
  const { documents, isAttaching } = useCreditDocumentUpload();
  const [attachDocumentTrigger, setAttachDocumentTrigger] = useState<number | null>(null);
  const [isDocumentAttached, setIsDocumentAttached] = useState(false);

  return (
    <Modal
      isOpen={isOpen}
      width={450}
      onClose={onClose}
      headerContent={<ModalHeader />}
      footerContent={
        <ModalFooter
          onClose={onClose}
          isAttaching={isAttaching}
          documents={documents}
          onAttach={() => setAttachDocumentTrigger(attachDocumentTrigger ? attachDocumentTrigger + 1 : 1)}
          isDocumentAttached={isDocumentAttached}
        />
      }
      zIndex={99}
    >
      <DocumentUploadForm
        attachDocumentTrigger={attachDocumentTrigger}
        credit={credit}
        onClose={onClose}
        isDocumentAttached={isDocumentAttached}
        setIsDocumentAttached={setIsDocumentAttached}
      />
    </Modal>
  );
};

export default DocumentUploadModal;
